import moment from 'moment-timezone';

import { countryCodesShort } from 'src/app/core/constants/country-codes';
import { currencies } from 'src/app/core/constants/currencies';
import { taxRegimes } from 'src/app/core/constants/tax-regimes';
import { uiLanguages } from 'src/app/core/constants/ui-languages';

import type { SettingOption, SettingsContext } from '../models/settings-hub.model';

export const PRICE_LISTS_COUNT = 10;

export function countryOptions(): SettingOption[] {
    return countryCodesShort.map(({ code, name }) => ({ value: code, label: name }));
}

export function currencyOptions(): SettingOption[] {
    return Object.values(currencies).map(({ code, symbol_native }) => ({ value: code, label: `${code} (${symbol_native})` }));
}

export function dayStartTimeOptions(): SettingOption[] {
    return Array.from({ length: 24 }, (_, hour) => ['00', '30'].map(minutes => {
        const time = `${String(hour).padStart(2, '0')}:${minutes}`;
        return { value: time, label: time };
    })).flat();
}

export function timezoneOptions(): SettingOption[] {
    return moment.tz.names().map(timezone => ({ value: timezone, label: timezone }));
}

export function uiLanguageOptions(): SettingOption[] {
    return Object.values(uiLanguages)
        .filter(language => !language.ui_hide)
        .map(({ id, value }) => ({ value: id, label: value }));
}

export function taxRegimeOptions(): SettingOption[] {
    return taxRegimes.map(({ id, name }) => ({ value: id, label: name }));
}

export function departmentOptions({ data }: SettingsContext): SettingOption[] {
    return data.departments.map(({ id, name }) => ({ value: id!, label: name ?? String(id) }));
}

export function expenseReportTemplateOptions({ data }: SettingsContext): SettingOption[] {
    return data.nonFiscalDocuments
        .filter(document => document.type === 'expense_report')
        .map(({ id, name }) => ({ value: id!, label: name ?? String(id) }));
}

/** The printers that can print a document, e.g. the one used by the automatic print */
export function documentPrinterOptions({ data }: SettingsContext): SettingOption[] {
    return data.documentPrinters.map(({ id, name }) => ({ value: id, label: name }));
}

/** The documents that the printer chosen for the automatic print can print */
export function autoPrintDocumentOptions({ values, data }: SettingsContext): SettingOption[] {
    const printerId = Number(values['cashregister.batch_sale_print.default_printer_id']);

    return data.documentPrinters.find(printer => printer.id === printerId)?.documents ?? [];
}

/** Numbers from `from` to `to` (included) */
export function rangeOptions(from: number, to: number, step = 1): SettingOption[] {
    return Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, index) => {
        const value = from + index * step;
        return { value, label: String(value) };
    });
}

/** The default exit can be any of the exits of the orders, or none (0) */
export function defaultExitOptions({ values }: SettingsContext): SettingOption[] {
    const exits = values['orders.exits'];
    const lastExit = typeof exits === 'number' ? exits : 10;

    return Array.from({ length: lastExit + 1 }, (_, exit) => exit === 0
        ? { value: 0, label: 'SETTINGS.SETTINGS.DEFAULT_EXIT_LABELS.NO_EXIT_LABEL', translate: true }
        : { value: exit, label: 'SETTINGS.SETTINGS.DEFAULT_EXIT_LABELS.EXIT', translate: true, labelParams: { value: exit } });
}

export function publicReceiptDurationOptions(): SettingOption[] {
    return [
        { value: 0, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.NONE' },
        { value: 24, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.1_DAY' },
        { value: 48, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.2_DAYS' },
        { value: 72, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.3_DAYS' },
        { value: 168, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.1_WEEK' },
        { value: 720, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.1_MONTH' },
        { value: 2160, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.3_MONTHS' },
        { value: 8640, label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.1_YEAR' }
    ].map(option => ({ ...option, translate: true }));
}

export function bookingWarningOptions(): SettingOption[] {
    return [0, 10, 15, 20, 30, 45, 60, 90, 120].map(minutes => minutes === 0
        ? { value: 0, label: 'SETTINGS.SETTINGS.TABLES_BOOKING_WARNING_LABEL.NONE', translate: true }
        : { value: minutes, label: 'SETTINGS.SETTINGS.TABLES_BOOKING_WARNING_LABEL.MINUTES', translate: true, labelParams: { minutes } });
}

export function queueModeOptions(): SettingOption[] {
    return [
        { value: '', label: 'SETTINGS.SETTINGS.QUEUE_MODE_DISABLED' },
        { value: 'auto', label: 'SETTINGS.SETTINGS.QUEUE_MODE_AUTO' },
        { value: 'manual', label: 'SETTINGS.SETTINGS.QUEUE_MODE_MANUAL' }
    ].map(option => ({ ...option, translate: true }));
}

export function queuePrintTypeOptions(): SettingOption[] {
    return [
        { value: 'tail', label: 'SETTINGS.SETTINGS.QUEUE_PRINT_TYPE_TAIL' },
        { value: 'non_fiscal', label: 'SETTINGS.SETTINGS.QUEUE_PRINT_TYPE_NON_FISCAL' }
    ].map(option => ({ ...option, translate: true }));
}

/** The price lists (none, then the ten price lists, named by the user or with a default name) */
export function priceListOptions({ values }: SettingsContext): SettingOption[] {
    return [
        { value: 0, label: 'SETTINGS.SETTINGS.NONE', translate: true },
        ...Array.from({ length: PRICE_LISTS_COUNT }, (_, index) => {
            const priceList = index + 1;
            const name = values[`price_list_${priceList}_name`];

            return name
                ? { value: priceList, label: String(name) }
                : { value: priceList, label: 'SETTINGS.SETTINGS.PRICE_LIST_DEFAULT_NAME', translate: true, labelParams: { value: priceList } };
        })
    ];
}

const PIENISSIMO_GIFTCARD_PAYMENT_TYPE = 10;
export const LEAN_PMS_PAYMENT_TYPE = 28;

/** The payment methods that can handle the giftcards of Pienissimo, or none */
export function pienissimoGiftcardMethodOptions({ data }: SettingsContext): SettingOption[] {
    return [
        { value: null, label: 'SETTINGS.SETTINGS.NONE', translate: true },
        ...data.paymentMethods
            .filter(paymentMethod => paymentMethod.payment_method_type_id === PIENISSIMO_GIFTCARD_PAYMENT_TYPE)
            .map(({ id, name }) => ({ value: id!, label: name ?? String(id) }))
    ];
}

/** The payment methods that are not Lean ones: the ones that can be mapped to a Lean payment code */
export function nonLeanPaymentMethodOptions({ data }: SettingsContext): SettingOption[] {
    return data.paymentMethods
        .filter(paymentMethod => paymentMethod.payment_method_type_id !== LEAN_PMS_PAYMENT_TYPE)
        .map(({ id, name }) => ({ value: id, label: name ?? String(id) }));
}

export function qrCodeSizeOptions(): SettingOption[] {
    return [{ value: 0, label: 'SETTINGS.SETTINGS.NONE', translate: true }, ...rangeOptions(1, 16)];
}
